import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import logger from "../../utils/logger";
import { Palette, Globe, Database, Save, Bell, Mail } from "lucide-react";

const API = `${window.location.origin}/api`;

export function WhiteLabelTab() {
  const [wl, setWl] = useState({ company_name: "", brand_color: "#2563eb", custom_domain: "", logo_url: "", data_retention_days: 2555 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/whitelabel`, { withCredentials: true });
        setWl(data.whitelabel || {});
      } catch (err) { logger.error("Failed to load whitelabel:", err); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/whitelabel`, wl, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { logger.error("Failed to save whitelabel:", err); }
    finally { setSaving(false); }
  };

  const inputStyle = { width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "13px", outline: "none", boxSizing: "border-box" };

  return (
    <div data-testid="whitelabel-tab" className="space-y-4">
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "18px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Palette style={{ width: "16px", height: "16px", color: "#FF6B35" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#f1f5f9" }}>White-Label Settings</span>
        </div>
        <div className="space-y-3">
          <div>
            <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: "4px" }}>Company Name</label>
            <input value={wl.company_name} onChange={e => setWl(p => ({ ...p, company_name: e.target.value }))} style={inputStyle} data-testid="wl-company-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: "4px" }}>Brand Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={wl.brand_color} onChange={e => setWl(p => ({ ...p, brand_color: e.target.value }))} style={{ width: "40px", height: "36px", borderRadius: "6px", border: "1px solid #1e2530", cursor: "pointer" }} data-testid="wl-brand-color" />
                <input value={wl.brand_color} onChange={e => setWl(p => ({ ...p, brand_color: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: "4px" }}>Custom Domain</label>
              <input value={wl.custom_domain} onChange={e => setWl(p => ({ ...p, custom_domain: e.target.value }))} placeholder="app.yourdomain.com" style={inputStyle} data-testid="wl-custom-domain" />
            </div>
          </div>
          <div>
            <label style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: "4px" }}>Logo URL</label>
            <input value={wl.logo_url} onChange={e => setWl(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://..." style={inputStyle} data-testid="wl-logo-url" />
          </div>
        </div>
      </div>

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "18px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Database style={{ width: "16px", height: "16px", color: "#2563eb" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#f1f5f9" }}>Data Retention Policy</span>
        </div>
        <div className="flex items-center gap-3">
          <input type="number" value={wl.data_retention_days} onChange={e => setWl(p => ({ ...p, data_retention_days: parseInt(e.target.value) || 0 }))} style={{ ...inputStyle, width: "120px", textAlign: "center" }} data-testid="wl-retention-days" />
          <span style={{ fontSize: "12px", color: "#94a3b8" }}>days ({Math.round((wl.data_retention_days || 0) / 365 * 10) / 10} years)</span>
        </div>
        <p style={{ fontSize: "10px", color: "#475569", marginTop: "6px" }}>RBI requires minimum 5 years (1825 days) retention for AML records</p>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} data-testid="wl-save-btn" style={{
          background: saved ? "rgba(16,185,129,0.12)" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
          border: saved ? "1px solid rgba(16,185,129,0.3)" : "none", borderRadius: "8px",
          padding: "10px 24px", cursor: "pointer", color: saved ? "#10b981" : "#fff", fontSize: "13px", fontWeight: 600,
          display: "flex", alignItems: "center", gap: "6px",
        }}>
          <Save style={{ width: "14px", height: "14px" }} />
          {saving ? "Saving..." : saved ? "Saved" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

export function NotificationPrefsTab() {
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/notification-prefs`, { withCredentials: true });
        setPrefs(data.prefs || {});
      } catch (err) { logger.error("Failed to load notification prefs:", err); }
    })();
  }, []);

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/notification-prefs`, prefs, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { logger.error("Failed to save prefs:", err); }
    finally { setSaving(false); }
  };

  const emailItems = [
    { key: "email_screening_match", label: "Screening Match Found" },
    { key: "email_case_escalation", label: "Case Escalation" },
    { key: "email_str_filing", label: "STR Filing Due" },
    { key: "email_sla_breach", label: "SLA Breach" },
  ];
  const whatsappItems = [
    { key: "whatsapp_critical_alerts", label: "Critical Alerts" },
    { key: "whatsapp_str_reminders", label: "STR Reminders" },
  ];

  return (
    <div data-testid="notification-prefs-tab" className="space-y-4">
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "18px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Mail style={{ width: "16px", height: "16px", color: "#2563eb" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#f1f5f9" }}>Email Notifications</span>
        </div>
        {emailItems.map(item => (
          <div key={item.key} className="flex items-center justify-between" style={{ padding: "8px 0", borderBottom: "1px solid #0f1520" }}>
            <span style={{ fontSize: "12px", color: "#f1f5f9" }}>{item.label}</span>
            <button onClick={() => toggle(item.key)} data-testid={`toggle-${item.key}`} style={{
              width: "40px", height: "22px", borderRadius: "11px", border: "none", cursor: "pointer",
              background: prefs[item.key] ? "#2563eb" : "#1e2530", position: "relative", transition: "background 0.2s",
            }}>
              <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: "#fff", position: "absolute", top: "3px", left: prefs[item.key] ? "21px" : "3px", transition: "left 0.2s" }} />
            </button>
          </div>
        ))}
      </div>

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "18px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Bell style={{ width: "16px", height: "16px", color: "#10b981" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#f1f5f9" }}>WhatsApp Notifications</span>
        </div>
        {whatsappItems.map(item => (
          <div key={item.key} className="flex items-center justify-between" style={{ padding: "8px 0", borderBottom: "1px solid #0f1520" }}>
            <span style={{ fontSize: "12px", color: "#f1f5f9" }}>{item.label}</span>
            <button onClick={() => toggle(item.key)} data-testid={`toggle-${item.key}`} style={{
              width: "40px", height: "22px", borderRadius: "11px", border: "none", cursor: "pointer",
              background: prefs[item.key] ? "#10b981" : "#1e2530", position: "relative", transition: "background 0.2s",
            }}>
              <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: "#fff", position: "absolute", top: "3px", left: prefs[item.key] ? "21px" : "3px", transition: "left 0.2s" }} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} data-testid="notif-save-btn" style={{
          background: saved ? "rgba(16,185,129,0.12)" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
          border: saved ? "1px solid rgba(16,185,129,0.3)" : "none", borderRadius: "8px",
          padding: "10px 24px", cursor: "pointer", color: saved ? "#10b981" : "#fff", fontSize: "13px", fontWeight: 600,
        }}>
          <Save style={{ width: "14px", height: "14px" }} />
          {saving ? "Saving..." : saved ? "Saved" : "Save Preferences"}
        </button>
      </div>
    </div>
  );
}
